import site from "../site";
import Helpers from "./helpers";
import TrapFocus from "./trap-focus";

export default class Popups {

	private allow = false;
	private fadeClick = true;
	private offset = 50;
	private backTo = false;
	private limit = 640;
	private reservedIds = ['popupVideo', 'popupConfirm', 'popupLoader']; // used for closing popups - these will NOT be removed
	private templatesLoaded = false;
	private lastFocusedElement: HTMLLinkElement;

	private static loaderHTML = '<div id="popupLoader" data-test="site-loader" class="mPopup noPrint"><div class="c-loader c-loader--inline c-loader--large"><span class="u-visuallyhidden">Loading..</span></div></div>';
	private static popupVideoHTML = '<div id="popupVideo" class="mPopup noPrint"><div class="mPopupInner"><h1 id="popupVideoTitle">Video</h1><iframe frameborder="0" width="480"></iframe></div><a href="#" class="mPopupClose js-popup-close">Close</a></div>';
	private static confirmPopupHTML = `
	<div id="popupConfirm" class="mPopup noPrint">
		<div class="mPopupInner">
			<h1 id="popupConfirmTitle">Confirm</h1>
			<p id="popupConfirmText"></p>
				<div class="formAction">
					<a id="popupConfirmYes" class="formButton" data-test="popup-yes-button" href="#" data-no-instant>Yes <span class="u-visuallyhidden">(closes overlay)</span></a>
					<button id="popupConfirmNo" class="formButtonGrey formButton js-popup-close" data-test="popup-no-button" type="button">No <span class="u-visuallyhidden">(closes overlay)</span></button>
				</div>
			<button class="mPopupClose js-popup-close" type="button"><span class="u-visuallyhidden">Close overlay</span></button>
		</div>
	</div>`;
	private static fadeHTML = '<div id="popupFade" class="mPopupFade noPrint js-popup-close js-popup-fade"></div>';


	constructor() {
		this.attachEvents();
	}

	private attachEvents() {

		document.addEventListener('click', (e) => {

			const targetElement = e.target as HTMLElement;

			if (targetElement.tagName !== "A" && !targetElement.classList.contains('js-popup-close')) {
				return;
			}

			// Normal
			if (targetElement.matches('a.popupLaunch')) {
				this.open(targetElement.getAttribute('href'), targetElement.getAttribute('data-width'));
				targetElement.classList.add('popupLaunchDone');
				e.preventDefault();
			}

			// YouTube
			if (targetElement.matches('a[href*="youtube.com/embed/"]')) {
				this.video(targetElement.getAttribute('href'), 'YouTube');
				targetElement.classList.add('popupVideoDone');
				e.preventDefault();
			}

			// Vimeo
			if (targetElement.matches('a[href*="player.vimeo.com/video/"]:not([data-gibe-gallery-item])')) {
				this.video(targetElement.getAttribute('href'), 'Vimeo');
				targetElement.classList.add('popupVideoDone');
				e.preventDefault();
			}

			// Confirm
			if (targetElement.matches('a.popupConfirm')) {
				let text = 'Are you sure? This cannot be undone.';
				if (typeof targetElement.getAttribute('data-text') !== 'undefined' && targetElement.getAttribute('data-text') !== '') {
					text = targetElement.getAttribute('data-text');
				}
				this.confirm('Confirm', text, targetElement.getAttribute('href'));
				targetElement.classList.add('popupLaunchDone');
				e.preventDefault();
			}

			// close
			if (targetElement.matches('.js-popup-close')) {
				if (this.fadeClick) {
					this.close();
				}
				targetElement.classList.add('popupCloseDone');
				e.preventDefault();
			}

		});

		// resize close
		const debouncedResizeCheck = site.debounce(() => {
			if (window.innerWidth < this.limit) {
				this.close();
			}
		}, 250, null);
		window.addEventListener('resize', debouncedResizeCheck);

		window.addEventListener('keydown', (e) => this.closeIfEscapeKeyPressed(e));
		
	}

	private closeIfEscapeKeyPressed(e: KeyboardEvent) {
		const closePopupFade = document.querySelector('.js-popup-fade') as HTMLButtonElement;
		if (!closePopupFade) {
			return;
		}
		const closePopupStyles = window.getComputedStyle(closePopupFade);
		if (closePopupStyles.display === 'block') {
			const key = Helpers.getKeypressed(e);
			const isEscapeKey = key === 'Escape' || key === 27;
			if (isEscapeKey) {
				closePopupFade.click();
			}
		}
	}

	private loadTemplates() {
		if (this.templatesLoaded) {
			return;
		}
		const elementsToInsert: string[] = [];
		elementsToInsert.push(Popups.loaderHTML);
		elementsToInsert.push(Popups.popupVideoHTML);
		if (document.querySelector('.popupConfirm')) {
			elementsToInsert.push(Popups.confirmPopupHTML);
		}
		elementsToInsert.push(Popups.fadeHTML);
		document.body.insertAdjacentHTML("beforeend", elementsToInsert.join(''));
		// allow
		this.allow = true;
		this.templatesLoaded = true;
	}

	private open(url: string, width: string) {

		this.loadTemplates();

		// if the popup url is the same as the page we're on, reload the page
		if (location.pathname == url) {
			location.href = url;
			return false;
		}

		if (this.allow && (site.win.width() < this.limit)) {
			window.open(url);
			return false;
		}
		else if (this.allow && typeof url != 'undefined') {
			const popupAlreadyOpen = document.querySelector('.mPopup') as HTMLDivElement;
			const popupIsVisible = popupAlreadyOpen.offsetParent !== null;
			if (popupIsVisible) {
				this.backTo = true;
				popupAlreadyOpen.setAttribute('data-hidden', 'true');
				popupAlreadyOpen.style.display = 'none';
			}
			// show loader
			this.loader();
			// load popup content
			document.body.insertAdjacentHTML("beforeend", '<div class="mPopup noPrint" data-href="' + url + '"><div class="mPopupInner"><div class="mPopupContent clearfix"></div><a href="#" class="mPopupClose js-popup-close">Close</a></div></div>');

			Helpers.loadHtml(encodeURI(url), 'popupContentWrapper')
				.then((response) => {
					const popupElement = document.querySelector('div[data-href="' + url + '"]') as HTMLDivElement;
					popupElement.querySelector('.mPopupContent').insertAdjacentElement('afterbegin', response);
					popupElement.removeAttribute('style');
					popupElement.style.top = this.getTop() + "px";

					// set width if required
					if (typeof width != 'undefined') {
						popupElement.style.marginLeft = ((parseInt(width) / 2) * -1) + "px";
						popupElement.style.width = parseInt(width, 10) + "px";
						popupElement.removeAttribute('data-hidden');
						popupElement.style.display = "block";
					}
					// hide loader
					this.loader(true);
					this.allow = true;
					this.fadeClick = true;
					// show popup
					popupElement.style.display = "block";

				})
				.catch(() => {
					// hide loader
					this.loader(true);
					this.allow = true;
					this.fadeClick = true;
					// show popup
					this.confirm('Error', 'Sorry, the URL "' + url + '" could not be shown.', '?', true);
				});
		}
	}

	private video(src: string, ref: string) {
		this.loadTemplates();
		const videoPopupElement = document.querySelector('#popupVideo') as HTMLDivElement;
		if (!videoPopupElement || (window.innerWidth < this.limit)) {
			window.open(src);
			return false;
		}
		else {
			// update src
			videoPopupElement.querySelector('iframe').setAttribute('src', src);
			// set top
			videoPopupElement.style.top = this.getTop() + "px";
			// show
			videoPopupElement.style.display = 'block';
			const popupFadeElement = document.querySelector('#popupFade') as HTMLElement;
			popupFadeElement.style.display = 'block';
		}
	}

	private confirm(title: string, text: string, url: string, notice?: boolean) {
		this.loadTemplates();
		const confirmPopupElement = document.querySelector('#popupConfirm') as HTMLDivElement;
		if (!this.allow) return false;
		// set info title
		confirmPopupElement.querySelector('#popupConfirmTitle').textContent = title;
		// set info text
		confirmPopupElement.querySelector('#popupConfirmText').innerHTML = text;
		// set links
		confirmPopupElement.querySelector('#popupConfirmYes').setAttribute('href', url);
		// set top
		confirmPopupElement.style.top = this.getTop() + 'px';
		// if notice
		const popupConfirmYesElement = confirmPopupElement.querySelector('#popupConfirmYes') as HTMLElement;
		const popupConfirmNoElement = confirmPopupElement.querySelector('#popupConfirmNo') as HTMLElement;
		if (notice) {
			if (popupConfirmYesElement) {
				popupConfirmYesElement.style.display = 'none';
			}
			if (popupConfirmNoElement) {
				popupConfirmNoElement.style.display = 'none';
			}
		}
		else {
			if (popupConfirmYesElement) {
				popupConfirmYesElement.style.display = 'inline-block';
			}
			if (popupConfirmNoElement) {
				popupConfirmNoElement.style.display = 'inline-block';
			}
		}
		// show
		confirmPopupElement.style.display = 'block';
		const popupFadeElement = document.querySelector('#popupFade') as HTMLElement;
		popupFadeElement.style.display = 'block';
		popupConfirmYesElement.addEventListener('click', (event) => {
			event.preventDefault();
			if (!popupConfirmYesElement.classList.contains('is-disabled')) {
				popupConfirmYesElement.classList.add('is-disabled');
				window.location.href = popupConfirmYesElement.getAttribute('href');
			}
		});

		// accessibility features
		this.lastFocusedElement = document.activeElement as HTMLLinkElement;
		const firstFocusableElement = confirmPopupElement.querySelector('#popupConfirmYes') as HTMLButtonElement;
		firstFocusableElement.focus();
		new TrapFocus(confirmPopupElement);
	}

	private loader(hide?: boolean) {
		if (!hide) {
			this.loadTemplates();
		}
		const popupLoaderElement = document.querySelector('#popupLoader') as HTMLDivElement;
		if (!popupLoaderElement) {
			return;
		}
		if (hide) {
			popupLoaderElement.style.display = 'none';
			this.allow = false;
			this.fadeClick = false;
			return;
		}
		if (this.allow) {
			const popupFadeElement = document.querySelector('#popupFade') as HTMLDivElement;
			popupLoaderElement.style.display = 'none';
			if (!popupLoaderElement || !popupFadeElement) return;
			this.allow = false;
			this.fadeClick = false;
			popupLoaderElement.style.display = 'block';
			if (popupFadeElement) {
				popupFadeElement.style.display = 'block';
			}
		}
	}

	private close(keepFade?: boolean) {
		const popupElements = document.querySelectorAll('.mPopup:not([data-visible]):not([style*="display: none"])') as NodeListOf<HTMLDivElement>;
		if (!this.allow) {
			return;
		}
		this.allow = false;
		// hide loader
		this.loader(true);
		// hide popups
		if (this.backTo) {
			this.backTo = false;
			const hiddenPopupElement = document.querySelector('.mPopup[data-hidden]') as HTMLDivElement;
			if (hiddenPopupElement) {
				hiddenPopupElement.removeAttribute('data-hidden');
				hiddenPopupElement.style.display = 'block';
			}
			if (popupElements.length) {
				for (let i = 0; i < popupElements.length; i++) {
					popupElements[i].style.display = 'none';
				}
			}
		}
		else {
			// remove fade
			if (!keepFade) {
				const popupFadeElement = document.querySelector('#popupFade') as HTMLDivElement;
				if (popupFadeElement) {
					popupFadeElement.style.display = 'none';
				}
			}
			// remove popups
			for (let i = 0; i < popupElements.length; i++) {
				if (this.reservedIds.indexOf(popupElements[i].id) === -1) {
					popupElements[i].style.display = 'none';
					popupElements[i].parentNode.removeChild(popupElements[i]);

				}
			}
			// hide remaining
			if (popupElements.length) {
				for (let i = 0; i < popupElements.length; i++) {
					popupElements[i].style.display = 'none';
				}
			}
		}
		// allow popups
		this.allow = true;
		// allow fade click
		this.fadeClick = true;

		if (this.lastFocusedElement) {
			this.lastFocusedElement.focus();
		}
	}

	private getTop() {
		const top = window.pageYOffset;
		const offset = this.offset;
		return top + offset;
	}

}

